import { SingleRowTable, Title, TitleTextPair } from './pdfContent'
import { isDependentFieldHidden } from '../../common/validation/functions'
import { capitalize, keyBy } from 'lodash'
import { LIKELIHOOD_OPTIONS, SEVERITY_OPTIONS } from '../../common/constants'

interface FormValueOptions {
  key: string
  isOptionField?: boolean
  isRiskField?: boolean
  values?: any[]
  formData: any
}

export const getFormValue = ({
  key,
  isOptionField,
  isRiskField,
  values,
  formData,
}: FormValueOptions) => {
  if (!formData.hasOwnProperty(key)) return
  let result
  const formValue = formData[key]
  if (!isOptionField && !isRiskField) {
    result = formValue instanceof Array ? formValue.join('\n') : formValue
  } else {
    // @ts-ignore
    const options = getOptionsFromFormValue(formValue, values, isRiskField)
    if (isRiskField) {
      if (options instanceof Array) {
        result = options
          .map((option) => `${option?.label} (Risk: ${option?.risk})`)
          .join('\n')
      } else {
        result = options.label
          ? `${options?.label} (Risk: ${options?.risk})`
          : `${capitalize(options.risk)} Risk`
      }
    } else if (options instanceof Array) {
      result = options.map((option) => option?.label).join('\n')
    } else {
      // options undefined
      result = options?.label || `${options?.value} (${options?.risk})`
    }
  }

  const comment = formData[`${key}_comment`]
  if (comment) result += `\n${comment}`

  return result
}

export const getOptionsFromFormValue = (
  formValue: any,
  options: any[],
  isRiskField: boolean
) => {
  const opts = keyBy(options, 'value')
  if (isRiskField) {
    if (formValue.length) formValue.map((index: number) => options[index])

    return formValue instanceof Array
      ? formValue.map((index) => options[index])
      : options[formValue]
  }

  return formValue instanceof Array
    ? formValue.map((value) => opts[value])
    : opts[formValue] || options[formValue]
}

interface OptionFieldFormat {
  key: string
  label: string
  formData: any // Replace with the actual type
  options: any[] // Replace with the actual type
  optionType: string
}

export const formatOptionField = ({
  key,
  label,
  formData,
  options,
  optionType,
}: OptionFieldFormat) =>
  TitleTextPair({
    title: label,
    titleStyle: 'tableHeader',
    text: getFormValue({
      key,
      formData,
      isOptionField: true,
      isRiskField: optionType === 'riskScore',
      values: options,
    }),
    textStyle: 'tableHeaderSmall',
  })

interface TextFieldFormat {
  key: string
  label: string
  formData: any // Replace with the actual type
}

export const formatTextField = ({ key, label, formData }: TextFieldFormat) =>
  TitleTextPair({
    title: label,
    titleStyle: 'tableHeader',
    text: getFormValue({ key, formData }),
    textStyle: 'tableHeaderSmall',
  })

interface FormFieldFormat {
  dependentFields: any[] // Replace with the actual type
  key: string
  label: string
  optionType: string
  suggestedActions: string
  type: string
  formData: any // Replace with the actual type
  values: any[] // Replace with the actual type
}

// @ts-ignore
export const formatFormField = ({
  dependentFields,
  key,
  label,
  optionType,
  suggestedActions,
  type,
  formData,
  values,
}: FormFieldFormat) => {
  let fieldContent = []
  if (!formData) return null

  switch (type) {
    case 'bmiCalculation':
      fieldContent.push(
        SingleRowTable({
          headerText: ['Height (cm)', 'Weight (kg)', 'BMI'],
          bodyText: ['height', 'weight', key].map((formKey) =>
            getFormValue({ key: formKey, formData })
          ),
        })
      )
      break
    case 'checkbox-group':
      fieldContent.push(
        formatOptionField({
          key,
          label,
          formData,
          options: values,
          optionType,
        })
      )
      break
    case 'header':
      fieldContent.push(Title({ title: label, style: 'h2' }))
      break
    case 'radio-group':
      fieldContent.push(
        formatOptionField({
          key,
          label,
          formData,
          options: values,
          optionType,
        })
      )
      break
    case 'riskCalculation':
      fieldContent.push(
        SingleRowTable({
          headerText: ['Likelihood', 'Severity', 'Outcome'],
          bodyText: [
            getFormValue({
              formData,
              key: 'likelihood',
              isRiskField: true,
              values: LIKELIHOOD_OPTIONS,
            }),
            getFormValue({
              formData,
              key: 'severity',
              isRiskField: true,
              values: SEVERITY_OPTIONS,
            }),
            getFormValue({ formData, key, isRiskField: true, values }),
          ],
        })
      )
      break
    case 'select':
      fieldContent.push(
        formatOptionField({
          key,
          label,
          formData,
          options: values,
          optionType,
        })
      )
      break
    case 'suggestedActions':
      fieldContent.push(
        TitleTextPair({
          title: label,
          // @ts-ignore
          formData,
          titleStyle: 'tableHeader',
          text: suggestedActions,
          textStyle: 'tableHeaderSmall',
        })
      )
      break
    default:
      fieldContent.push(formatTextField({ key, label, formData }))
  }

  if (dependentFields && dependentFields.length) {
    fieldContent = [
      ...fieldContent,
      ...dependentFields
        .filter((field) => !isDependentFieldHidden(formData, field.dependsOn))
        // @ts-ignore
        .map((field) => formatFormField({ ...field, formData })),
    ]
  }

  return fieldContent
}
